import { Link, useLocation } from "wouter";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/components/ThemeProvider";
import {
  LayoutDashboard, Store, Users, Star, LogOut, Menu, X, Sun, Moon,
  Shield, Crown, Smartphone, Cloud, Plug, Globe, Mail, ShieldAlert,
  Megaphone, Phone, PackagePlus, Gift, Newspaper, LifeBuoy, BookOpen, MapPinned, ListTree, MailOpen
} from "lucide-react";
import { useState, type ReactNode } from "react";

const navSections = [
  {
    label: "Overview",
    items: [
      { href: "/admin", icon: LayoutDashboard, label: "Dashboard" },
    ],
  },
  {
    label: "Super Admin",
    items: [
      { href: "/admin/shop-discovery", icon: MapPinned, label: "Shop Discovery" },
      { href: "/admin/directory", icon: ListTree, label: "Directory" },
      { href: "/admin/outreach", icon: Megaphone, label: "Outreach" },
      { href: "/admin/integrations", icon: Plug, label: "Integrations" },
      { href: "/admin/catalog", icon: Smartphone, label: "Device Catalog" },
      { href: "/admin/phone-numbers", icon: Phone, label: "Phone Numbers" },
    ],
  },
  {
    label: "Management",
    items: [
      { href: "/admin/shops", icon: Store, label: "Shops" },
      { href: "/admin/users", icon: Users, label: "Users" },
      { href: "/admin/subscriptions", icon: Crown, label: "Subscriptions" },
      { href: "/admin/addon-packages", icon: PackagePlus, label: "Add-on Packs" },
      { href: "/admin/referrals", icon: Gift, label: "Referrals" },
      { href: "/admin/reviews", icon: Star, label: "Reviews" },
      { href: "/admin/contact-requests", icon: Mail, label: "Contact Requests" },
    ],
  },
  {
    label: "Platform",
    items: [
      { href: "/admin/reserved-usernames", icon: ShieldAlert, label: "Reserved Names" },
      { href: "/admin/storage", icon: Cloud, label: "Storage" },
      { href: "/admin/seo", icon: Globe, label: "SEO" },
      { href: "/admin/blog", icon: Newspaper, label: "Blog" },
      { href: "/admin/email-templates", icon: MailOpen, label: "Email Templates" },
    ],
  },
  {
    label: "Help Center",
    items: [
      { href: "/admin/support-tickets", icon: LifeBuoy, label: "Support Tickets" },
      { href: "/admin/user-guide", icon: BookOpen, label: "User Guide" },
    ],
  },
];

export default function AdminLayout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const [location] = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-background">
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-sidebar border-r border-sidebar-border transform transition-transform lg:translate-x-0 lg:static lg:inset-auto ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex flex-col h-full">
          <div className="h-16 flex items-center justify-between px-4 border-b border-sidebar-border">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <span className="text-lg font-bold text-primary">Admin</span>
            </div>
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <nav className="flex-1 px-3 py-4 overflow-y-auto space-y-5">
            {navSections.map((section) => (
              <div key={section.label}>
                <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-sidebar-foreground/40 select-none">
                  {section.label}
                </p>
                <div className="space-y-0.5">
                  {section.items.map(({ href, icon: Icon, label }) => {
                    const isActive = location === href;
                    return (
                      <Link key={href} href={href}>
                        <div
                          className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm cursor-pointer transition-colors ${isActive ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium" : "text-sidebar-foreground hover:bg-sidebar-accent/50"}`}
                          data-testid={`nav-admin-${label.toLowerCase().replace(/\s+/g, "-")}`}
                          onClick={() => setSidebarOpen(false)}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          {label}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          <div className="p-4 border-t border-sidebar-border">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary">
                {user?.fullName?.charAt(0) || "A"}
              </div>
              <div>
                <p className="text-sm font-medium truncate max-w-[130px]">{user?.fullName}</p>
                <p className="text-xs text-muted-foreground">Super Admin</p>
              </div>
            </div>
            <Link href="/">
              <Button variant="ghost" size="sm" className="w-full justify-start mb-1">Back to Site</Button>
            </Link>
            <Button variant="ghost" size="sm" className="w-full justify-start" onClick={logout} data-testid="button-admin-logout">
              <LogOut className="h-4 w-4 mr-2" /> Log Out
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b bg-card flex items-center justify-between px-4 lg:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex-1" />
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </Button>
        </header>
        <main className="flex-1 p-4 lg:p-6 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
